import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { useForm } from 'react-hook-form';
import { listLookup } from '../../api/masterData';
import { ConvertInquiryPayload } from '../../api/inquiries';
import { InquiryListItem } from '../../types/inquiry';

interface ConvertFormValues {
  leadSourceId: number | '';
  countryId: number | '';
}

interface ConvertInquiryDialogProps {
  inquiry: InquiryListItem | null;
  errorMessage: string | null;
  onClose: () => void;
  onSubmit: (payload: ConvertInquiryPayload) => Promise<void>;
}

export const ConvertInquiryDialog = ({ inquiry, errorMessage, onClose, onSubmit }: ConvertInquiryDialogProps) => {
  const {
    register,
    handleSubmit,
    reset,
    formState: { isSubmitting },
  } = useForm<ConvertFormValues>({ defaultValues: { leadSourceId: '', countryId: '' } });

  const sourcesQuery = useQuery({
    queryKey: ['lookup', '/master-data/lead-sources'],
    queryFn: () => listLookup('/master-data/lead-sources', { activeOnly: true }),
  });
  const countriesQuery = useQuery({
    queryKey: ['lookup', '/master-data/countries'],
    queryFn: () => listLookup('/master-data/countries', { activeOnly: true }),
  });

  useEffect(() => {
    if (inquiry) {
      const websiteSource = sourcesQuery.data?.find((s) => s.name === 'Website');
      reset({ leadSourceId: (websiteSource?.id as number) ?? '', countryId: '' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inquiry, sourcesQuery.data]);

  const submit = async (values: ConvertFormValues) => {
    await onSubmit({
      leadSourceId: values.leadSourceId ? Number(values.leadSourceId) : undefined,
      countryId: values.countryId ? Number(values.countryId) : undefined,
    });
  };

  return (
    <Dialog open={!!inquiry} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Convert to lead</DialogTitle>
      <Stack component="form" onSubmit={handleSubmit(submit)} noValidate>
        <DialogContent>
          <Stack spacing={2}>
            {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
            {inquiry && (
              <Typography variant="body2" color="text.secondary">
                {inquiry.firstName} {inquiry.lastName} · {inquiry.phone}
                {inquiry.countryOfInterest ? ` · Interested in ${inquiry.countryOfInterest}` : ''}
              </Typography>
            )}
            <TextField select label="Lead source" fullWidth {...register('leadSourceId')}>
              {!sourcesQuery.data?.length && (
                <MenuItem value="" disabled>
                  <em>Loading…</em>
                </MenuItem>
              )}
              {sourcesQuery.data?.map((s) => (
                <MenuItem key={s.id as number} value={s.id as number}>
                  {String(s.name)}
                </MenuItem>
              ))}
            </TextField>
            <TextField select label="Country (optional)" fullWidth {...register('countryId')}>
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {countriesQuery.data?.map((c) => (
                <MenuItem key={c.id as number} value={c.id as number}>
                  {String(c.name)}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={isSubmitting}>
            {isSubmitting ? 'Converting…' : 'Convert to lead'}
          </Button>
        </DialogActions>
      </Stack>
    </Dialog>
  );
};
